import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navItems, siteInfo } from '@/mocks/brand';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/999b20db-023a-415b-8938-ab1563276c79/adab1eea-aa1c-4be9-9113-c915a6c79d56_compressed_-01.webp';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNavClick = () => {
    window.scrollTo(0, 0);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/home';
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out ${
        scrolled
          ? 'bg-black/15 backdrop-blur-xl border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
    >
      <div
        className={`mx-auto flex items-center justify-between px-6 md:px-10 max-w-[1440px] transition-all duration-300 ease-out ${
          scrolled ? 'h-[56px] md:h-[60px]' : 'h-[64px] md:h-[72px]'
        }`}
      >
        <Link
          to="/"
          onClick={handleNavClick}
          className="flex items-center gap-2.5 shrink-0 cursor-pointer"
        >
          <img
            src={LOGO_URL}
            alt="沐燁文化 Moya"
            className={`object-contain rounded-md transition-all duration-300 ease-out ${
              scrolled ? 'h-7 w-7' : 'h-9 w-9'
            }`}
          />
          <span
            className={`font-heading font-bold tracking-wide transition-all duration-300 ease-out ${
              scrolled
                ? 'text-lg md:text-xl text-background-50'
                : 'text-xl md:text-2xl text-primary-900'
            }`}
          >
            沐燁文化 Moya
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={`relative px-4 py-2 font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-sm' : 'text-sm'
              } ${
                isActive(item.path)
                  ? scrolled
                    ? 'text-accent-400'
                    : 'text-primary-600'
                  : scrolled
                    ? 'text-background-50/70 hover:text-background-50'
                    : 'text-foreground-600 hover:text-primary-500'
              }`}
            >
              <LocalizedNavLabel href={item.path} fallback={item.label} />
              {isActive(item.path) && (
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] rounded-full transition-colors duration-200 ${
                    scrolled ? 'bg-accent-400' : 'bg-primary-500'
                  }`}
                />
              )}
            </Link>
          ))}
          <a
            href={siteInfo.catalogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`relative px-4 py-2 font-medium transition-all duration-200 whitespace-nowrap cursor-pointer text-sm flex items-center gap-1 ${
              scrolled
                ? 'text-background-50/70 hover:text-background-50'
                : 'text-foreground-600 hover:text-primary-500'
            }`}
          >
            書籍資料庫
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-external-link-line text-[10px]"></i>
            </span>
          </a>
        </div>

        <LanguageSwitcher />

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-200 ${
            scrolled ? 'text-background-50' : 'text-primary-800'
          }`}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className={`md:hidden border-t ${scrolled ? 'bg-black/30 backdrop-blur-xl border-white/[0.06]' : 'bg-background-50 border-background-200'}`}>
          <div className="flex flex-col px-6 py-4 gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer ${
                  isActive(item.path)
                    ? scrolled
                      ? 'bg-white/10 text-accent-400'
                      : 'bg-primary-50 text-primary-700'
                    : scrolled
                      ? 'text-background-50/70 hover:bg-white/10 hover:text-background-50'
                      : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <LocalizedNavLabel href={item.path} fallback={item.label} />
              </Link>
            ))}
            <a
              href={siteInfo.catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer flex items-center gap-1.5 ${
                scrolled
                  ? 'text-background-50/70 hover:bg-white/10 hover:text-background-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              書籍資料庫
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-external-link-line text-xs"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}